import { useState } from 'react';

import { ApiError } from '@solar/api-client';
import {
  APT_TYPE_LABEL,
  DOC_STATE_LABEL,
  ErrorBox,
  Field,
  GU_STATE_LABEL,
  INSTALL_COMPLETE_LABEL,
  ORDER_STATE_LABEL,
  Spinner,
  formatDate,
  formatDateTime,
  displayAddress,
  formatPhone,
  useAsync,
  won,
} from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

export function OrderDetail({
  orderId,
  onClose,
  onChanged,
}: {
  orderId: number;
  onClose: () => void;
  /** 철거 신청 등으로 목록에 반영할 변경이 생겼을 때. */
  onChanged?: () => void;
}) {
  const order = useAsync(() => api.getOrder(orderId), [orderId]);
  const [invite, setInvite] = useState<{ url: string; expires_at: string; sms_sent: boolean } | null>(
    null,
  );
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sendSms, setSendSms] = useState(true);
  // 계약 서식 — 기본은 "지역 자동 선택"(template_code 생략 → 서버가 주문 주소로
  // 서울/대구/인천 서식을 고른다). 예외 건만 목록에서 직접 고른다.
  const templates = useAsync(() => api.listContractTemplates(), []);
  const [templateCode, setTemplateCode] = useState('');

  // 철거 신청 — 레거시 OrderController@OrderUninstall 파리티. 확인 모달에서
  // 철거 일자/철거 비용(선택)을 받아 uninstall_status='Y' 로 만든다.
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const [uninstallDate, setUninstallDate] = useState('');
  const [uninstallPayment, setUninstallPayment] = useState('');
  const [uninstallBusy, setUninstallBusy] = useState(false);
  const [uninstallError, setUninstallError] = useState<string | null>(null);
  const [uninstallDone, setUninstallDone] = useState(false);

  async function requestUninstall() {
    setUninstallBusy(true);
    setUninstallError(null);
    try {
      const payment = Number(uninstallPayment);
      await api.requestUninstall(orderId, {
        uninstall_status: 'Y',
        uninstall_date: uninstallDate || undefined,
        uninstall_payment:
          uninstallPayment.trim() !== '' && Number.isFinite(payment) ? payment : undefined,
      });
      setUninstallDone(true);
      setUninstallOpen(false);
      order.reload();
      onChanged?.();
    } catch (err) {
      setUninstallError(err instanceof ApiError ? err.message : '철거 신청에 실패했습니다.');
    } finally {
      setUninstallBusy(false);
    }
  }

  // A/S 접수 — 레거시 "AS 등록" 파리티. 접수 내용만 받아 미배정(1) 상태로 만든다.
  const [asOpen, setAsOpen] = useState(false);
  const [asEtc, setAsEtc] = useState('');
  const [asBusy, setAsBusy] = useState(false);
  const [asError, setAsError] = useState<string | null>(null);
  const [asCreated, setAsCreated] = useState<number | null>(null);

  async function createAs() {
    setAsBusy(true);
    setAsError(null);
    try {
      const created = await api.createAs({ build_order_id: orderId, etc: asEtc.trim() });
      setAsCreated(created.id);
      setAsOpen(false);
      setAsEtc('');
    } catch (err) {
      setAsError(err instanceof ApiError ? err.message : 'A/S 를 접수하지 못했습니다.');
    } finally {
      setAsBusy(false);
    }
  }

  async function createInvite() {
    setBusy(true);
    setInviteError(null);
    try {
      // 링크는 서버가 만들고 토큰은 해시로만 저장된다. 레거시는 난수를 평문으로
      // 보관하고 만료도 없어 링크가 영구 유효했다.
      setInvite(
        await api.createInvite(orderId, {
          template_code: templateCode || undefined,
          send_sms: sendSms,
        }),
      );
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : '링크를 발급하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function openPdf() {
    setInviteError(null);
    try {
      const { url } = await api.contractPdf(orderId);
      if (!url) {
        setInviteError('아직 서명된 계약서가 없습니다.');
        return;
      }
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : '계약서를 열지 못했습니다.');
    }
  }

  if (order.loading) return <Spinner />;
  if (order.error) return <ErrorBox message={order.error} onRetry={order.reload} />;
  if (!order.data) return null;

  const o = order.data;

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{o.user_name ?? `#${o.id}`}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel">
        <Field label="상태">{ORDER_STATE_LABEL[o.state_type] ?? o.state_type}</Field>
        <Field label="연락처">{formatPhone(o.user_phone)}</Field>
        <Field label="주소">
          {displayAddress(o.addr_full, o.building_name, o.apart_number)}
        </Field>
        <Field label="건물유형">{APT_TYPE_LABEL[o.apt_type ?? 0] ?? '-'}</Field>
        <Field label="설치위치">{o.place_install ?? '-'}</Field>
        <Field label="제품">{o.product_name ?? '-'}</Field>
        <Field label="담당기사">{o.engineer_name ?? '미배정'}</Field>
        <Field label="설치일">{formatDate(o.install_date)}</Field>
        <Field label="설치여부">
          {INSTALL_COMPLETE_LABEL[o.install_complete ?? ''] ?? '-'}
        </Field>
      </section>

      <h3 className="section">보조금 / 정산</h3>
      <section className="panel">
        <Field label="총사업비">{won(o.price)}</Field>
        {/* 정부지원금(국비)은 2026 보급사업부터 — 서버가 아직 안 내리면(undefined) 숨긴다. */}
        {o.price_gov !== undefined && <Field label="정부지원금">{won(o.price_gov)}</Field>}
        <Field label="시 보조">{won(o.price_si)}</Field>
        <Field label="구 보조">{won(o.price_borough)}</Field>
        <Field label="자부담금">{won(o.payment)}</Field>
        <Field label="수납">{o.payment_check === 'Y' ? `완료 (${o.payment_type ?? '-'})` : '미수납'}</Field>
        <Field label="서류상태">{DOC_STATE_LABEL[o.state_doc ?? 0] ?? '-'}</Field>
        <Field label="구 지급">{GU_STATE_LABEL[o.state_gu ?? 0] ?? '-'}</Field>
      </section>

      <h3 className="section">철거</h3>
      <section className="panel">
        {/* 서버 계약: uninstall_status/uninstall_date/uninstall_payment 3필드.
            배포 순서에 따라 아직 안 내려올 수 있으므로 undefined 면 신청 UI 만 보인다. */}
        {o.uninstall_status === 'Y' ? (
          <>
            <Field label="상태">
              <span className="badge uninstall">철거 신청됨</span>
            </Field>
            <Field label="철거 일자">{formatDate(o.uninstall_date)}</Field>
            <Field label="철거 비용">{won(o.uninstall_payment)}</Field>
          </>
        ) : (
          <div className="row">
            <button type="button" onClick={() => setUninstallOpen(true)}>
              철거 신청
            </button>
          </div>
        )}
        {uninstallDone && <p className="ok">철거 신청이 되었습니다. 철거 관리 메뉴에서 확인하세요.</p>}
        {uninstallError && !uninstallOpen && <p className="error">{uninstallError}</p>}
      </section>

      {uninstallOpen && (
        <Modal title="철거 신청" onClose={() => setUninstallOpen(false)}>
          <p>
            <strong>{o.user_name ?? `주문 #${o.id}`}</strong> 주문을 철거 신청합니다. 신청하면
            철거 관리 목록에 나타납니다.
          </p>
          <label className="labeled">
            철거 일자
            <input
              type="date"
              value={uninstallDate}
              onChange={(e) => setUninstallDate(e.target.value)}
            />
          </label>
          <label className="labeled">
            철거 비용
            <input
              type="number"
              min={0}
              value={uninstallPayment}
              onChange={(e) => setUninstallPayment(e.target.value)}
              placeholder="원"
            />
          </label>
          {uninstallError && <p className="error">{uninstallError}</p>}
          <div className="row">
            <button type="button" onClick={() => setUninstallOpen(false)}>
              취소
            </button>
            <button
              type="button"
              className="primary"
              disabled={uninstallBusy}
              onClick={() => void requestUninstall()}
            >
              {uninstallBusy ? '신청 중…' : '철거 신청'}
            </button>
          </div>
        </Modal>
      )}

      <h3 className="section">현장 사진 ({o.photos.length})</h3>
      {o.photos.length === 0 ? (
        <p className="muted">등록된 사진이 없습니다.</p>
      ) : (
        <div className="thumbs">
          {o.photos.map((p) => (
            <a key={p.slot} href={p.url} target="_blank" rel="noopener noreferrer">
              <img src={p.url} alt={`슬롯 ${p.slot}`} loading="lazy" />
              <span>{formatDateTime(p.taken_at)}</span>
            </a>
          ))}
        </div>
      )}

      <h3 className="section">전자계약</h3>
      <section className="panel">
        <Field label="서명 완료">{o.contract_count}건</Field>

        <label className="labeled">
          계약 서식
          <select value={templateCode} onChange={(e) => setTemplateCode(e.target.value)}>
            <option value="">지역 자동 선택</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.code} value={t.code}>
                {t.title} ({t.region ?? '공통'} · v{t.version})
              </option>
            ))}
          </select>
        </label>
        {templates.error && <p className="error">서식 목록을 불러오지 못했습니다 — 지역 자동 선택으로 발급됩니다.</p>}

        <label className="check">
          <input
            type="checkbox"
            checked={sendSms}
            onChange={(e) => setSendSms(e.target.checked)}
          />
          발급과 동시에 SMS 발송
        </label>

        <div className="row">
          <button type="button" onClick={() => void createInvite()} disabled={busy}>
            {busy ? '발급 중…' : '서명 링크 발급'}
          </button>
          <button type="button" onClick={() => void openPdf()} disabled={o.contract_count === 0}>
            계약서 PDF
          </button>
        </div>

        {invite && (
          <div className="invite">
            <p>
              {invite.sms_sent ? 'SMS 를 발송했습니다.' : 'SMS 발송 없이 링크만 발급했습니다.'}{' '}
              만료 {formatDateTime(invite.expires_at)}
            </p>
            <input readOnly value={invite.url} onFocus={(e) => e.currentTarget.select()} />
          </div>
        )}
        {inviteError && <p className="error">{inviteError}</p>}
      </section>

      <h3 className="section">A/S</h3>
      <section className="panel">
        {!asOpen ? (
          <div className="row">
            <button type="button" onClick={() => setAsOpen(true)}>
              A/S 접수
            </button>
          </div>
        ) : (
          <>
            <textarea
              rows={3}
              value={asEtc}
              onChange={(e) => setAsEtc(e.target.value)}
              placeholder="접수 내용 (필수)"
            />
            <div className="row">
              <button type="button" onClick={() => setAsOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="primary"
                disabled={asBusy || asEtc.trim().length === 0}
                onClick={() => void createAs()}
              >
                {asBusy ? '접수 중…' : '접수'}
              </button>
            </div>
          </>
        )}
        {asCreated !== null && (
          <p className="ok">A/S #{asCreated} 이(가) 접수되었습니다. AS 관리 메뉴에서 배정하세요.</p>
        )}
        {asError && <p className="error">{asError}</p>}
      </section>

      <h3 className="section">메모</h3>
      {o.memos.length === 0 ? (
        <p className="muted">메모가 없습니다.</p>
      ) : (
        <ul className="memos">
          {o.memos.map((m) => (
            <li key={m.id}>
              <p>{m.memo}</p>
              <span className="muted">
                {m.user_name ?? '-'} · {formatDateTime(m.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
